import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, message, Spin, Alert, Tooltip } from 'antd';
import {
  ExclamationCircleOutlined, UserOutlined, UpOutlined, DownOutlined,
  BarChartOutlined, FolderOutlined, SettingOutlined, UnorderedListOutlined,
  FullscreenOutlined, FullscreenExitOutlined, LeftOutlined, RightOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { throttle } from 'lodash';

import { getUserDetails, hasStudyViewingPermission, isValidDate } from '../../../../utils/helper';
import { TokenStorage } from '../../../../utils/cookieStorage';
import { useReportStatus } from '../../../../hooks/useReportStatus';
import { launchViewerOnDualMonitor } from '../../../../utils/MonitorManager';
import { getEditorConfig, applyTemplateToDocument } from '../../../OnlyOfficeDocuments/documentsApi';
import useOnlyOfficeScript from '../../../OnlyOfficeDocuments/useOnlyOfficeScript';
import TemplatePanel from '../../../OnlyOfficeDocuments/TemplatePanel';

import { useReportDataOodocs } from '../../hooks/useReportDataOodocs';
import { useReportActionsOodocs } from '../../hooks/useReportActionsOodocs';
import { useReportSession } from '../../hooks/useReportSession';
import { REPORT_CONFIG } from '../../constants';
import type { OrderDetails, OoReport, Report } from '../../types';

import StatusActions from './StatusActions';
import StudyQueue from '../StudyQueue/StudyQueue';
import ReportHistory from '../Panels/ReportHistory';
import PriorReports from '../Panels/PriorReports';
import AdvancedOptions from '../Panels/AdvancedOptions';
import CriticalFindingDialog from '../Dialogs/CriticalFindingDialog';
import TemplateLoadDialog from '../Dialogs/TemplateLoadDialog';
import ViewNotesModal from '../Dialogs/ViewNotesModal';

import '../../styles/editor.css';

interface OOReportEditorProps {
  patientDetails: OrderDetails | null;
  /** Studies queue rendered at the top of the left rail */
  orders: OrderDetails[];
  selectedIndex: number;
  queueLoading?: boolean;
  onSelectOrder: (order: OrderDetails, index: number) => void;
  handlePostSaving?: (status: string, patDetails: OrderDetails) => void;
  cancel: () => void;
  focusMode?: boolean;
  onToggleFocusMode?: () => void;
}

const OOReportEditor: React.FC<OOReportEditorProps> = ({
  patientDetails, orders, selectedIndex, queueLoading = false, onSelectOrder,
  handlePostSaving, cancel, focusMode = false, onToggleFocusMode,
}) => {
  const editorInstanceRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastActivityRef = useRef(Date.now());
  // true when the doc was auto-created in this session (no prior content)
  const isNewDocRef = useRef(false);

  const [activeDocId, setActiveDocId] = useState<number | null>(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [onlyOfficeUrl, setOnlyOfficeUrl] = useState<string | null>(null);
  const [editorConfig, setEditorConfig] = useState<any>(null);
  const [proxyDraftUser, setProxyDraftUser] = useState<string | null>(null);
  const [proxySignoffUser, setProxySignoffUser] = useState<string | null>(null);
  const [cosigningDoctor, setCosigningDoctor] = useState<string | null>(null);
  const [diagnosed, setDiagnosed] = useState<string | null>(null);
  const [correlated, setCorrelated] = useState<string | null>(null);
  const [criticalFindingVisible, setCriticalFindingVisible] = useState(false);
  const [isPageIdle, setIsPageIdle] = useState(false);
  const [studiesCollapsed, setStudiesCollapsed] = useState(false);
  const [detailsCollapsed, setDetailsCollapsed] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    reportsHistory: true,
    priorReports: false,
    advancedOptions: true,
  });

  const pacs_order = patientDetails?.pacs_order;
  const patient = pacs_order?.patient;

  // ── Hooks ──
  const { statusList, loading: statusLoading } = useReportStatus();
  const {
    reports, currentReport, setCurrentReport,
    priorReports, radUsers, allUsers,
    isSignedOff, initialFetchDone, refetchReports,
  } = useReportDataOodocs(patientDetails);

  const onSaved = useCallback((status: string) => {
    message.success(status === 'SIGNEDOFF' ? 'Report signed off' : 'Updated successfully');
    // Sign-off snapshots the working draft into a new immutable signed report; the editor stays
    // on the (unchanged) draft so further amendments keep editing it. No doc switch / reload.
    refetchReports();
    handlePostSaving?.(status, patientDetails);
  }, [refetchReports, handlePostSaving, patientDetails]);

  const { saveReport, deleteReport, copyPriorReport, createNewDocument, createAmendmentDraft, saveAsDraft } = useReportActionsOodocs({
    orderDetails: patientDetails,
    currentReport,
    activeDocId,
    isSignedOff,
    onSaved,
  });

  useReportSession(pacs_order?.pacs_ord_id, !isPageIdle);

  const { loaded: scriptLoaded, error: scriptError, retry: retryScript } = useOnlyOfficeScript(onlyOfficeUrl);

  // ── Set active doc from current report ──
  // Editing always targets an editable working draft. If the only report available is a signed
  // (finalized) snapshot, seed an amendment draft from it so edits never touch the snapshot.
  useEffect(() => {
    if (!initialFetchDone) return; // wait for first fetch before deciding

    const doc = currentReport?.document;
    if (doc?.doc_id && doc.doc_status !== 'finalized') {
      setActiveDocId(doc.doc_id);
    } else if (doc?.doc_id && doc.doc_status === 'finalized') {
      // Only a signed snapshot exists — create an editable draft copy and open that.
      createAmendmentDraft(doc.doc_id).then(docId => {
        if (docId) {
          setActiveDocId(docId);
          refetchReports();
        } else {
          // Could not seed a draft — fall back to opening the snapshot (view-only, no autosave).
          setActiveDocId(doc.doc_id);
        }
      });
    } else if (currentReport === null && reports.length === 0) {
      // No reports exist yet — auto-create a document
      createNewDocument().then(docId => {
        if (docId) {
          isNewDocRef.current = true;
          setActiveDocId(docId);
          refetchReports();
        }
      });
    }
  }, [currentReport?.pr_id, initialFetchDone]);

  // ── Fetch OnlyOffice editor config when activeDocId changes ──
  const loadEditorConfig = useCallback(async (docId: number) => {
    setEditorLoading(true);
    setEditorError(null);
    setEditorConfig(null);

    try {
      const res = await getEditorConfig(docId);
      const { config, onlyOfficeUrl: url } = res.data.data;
      setEditorConfig(config);
      setOnlyOfficeUrl(url);
    } catch (err: any) {
      setEditorError(err.response?.data?.message || 'Failed to load editor configuration');
      setEditorLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!activeDocId) return;
    destroyEditor();
    loadEditorConfig(activeDocId);
  }, [activeDocId]);

  // ── Initialize OnlyOffice editor once script + config are ready ──
  useEffect(() => {
    if (!scriptLoaded || !editorConfig || !containerRef.current) return;

    const timer = setTimeout(() => {
      try {
        editorInstanceRef.current = new (window as any).DocsAPI.DocEditor(
          'oo-report-editor-container',
          {
            ...editorConfig,
            events: {
              ...editorConfig.events,
              onDocumentReady: () => setEditorLoading(false),
              onError: (event: any) => {
                console.error('OnlyOffice editor error:', event);
                message.error('Editor encountered an error');
              },
            },
          },
        );
      } catch (err) {
        console.error('Failed to initialize OnlyOffice editor:', err);
        setEditorError('Failed to initialize editor');
        setEditorLoading(false);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [scriptLoaded, editorConfig]);

  // ── Editor cleanup helper ──
  const destroyEditor = useCallback(() => {
    if (editorInstanceRef.current) {
      try { editorInstanceRef.current.destroyEditor(); } catch {}
      editorInstanceRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => () => destroyEditor(), []);

  // ── Idle detection (same as ReportEditor) ──
  useEffect(() => {
    const IDLE_TIME = 60 * 1000;

    const resetIdleTimer = () => {
      lastActivityRef.current = Date.now();
      if (isPageIdle) setIsPageIdle(false);
    };

    const throttledReset = throttle(resetIdleTimer, 1000, { leading: true, trailing: false });

    const checkIdle = () => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= IDLE_TIME && !isPageIdle) setIsPageIdle(true);
      else if (elapsed < IDLE_TIME && isPageIdle) setIsPageIdle(false);
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(e => document.addEventListener(e, throttledReset, true));
    const interval = setInterval(checkIdle, 30_000);

    return () => {
      events.forEach(e => document.removeEventListener(e, throttledReset, true));
      clearInterval(interval);
    };
  }, [isPageIdle]);

  // ── OnlyOffice dictation plugin integration ──
  useEffect(() => {
    const handlePluginMessage = (event: MessageEvent) => {
      if (event.data?.type === 'pacs_request_dictation_token') {
        try {
          const token = TokenStorage.getAccessToken();
          if (token && event.source) {
            (event.source as any).postMessage(
              { type: 'pacs_dictation_token', token },
              '*'
            );
          }
        } catch (error) {
          console.error('Failed to send dictation token:', error);
        }
      }
    };

    window.addEventListener('message', handlePluginMessage);

    return () => {
      window.removeEventListener('message', handlePluginMessage);
    };
  }, []);

  // ── Handlers ──
  const proxyMode = proxyDraftUser ? 'draft' : proxySignoffUser ? 'signoff' : null;

  // True when the editor is showing a signed (finalized) snapshot rather than the working draft —
  // it is read-only; the user must load the draft from Reports History to make edits.
  const viewingSigned = (reports as OoReport[]).some(
    r => r.document?.doc_id === activeDocId && r.document?.doc_status === 'finalized'
  );

  const handleSaveForm = useCallback((statusCode: string) => {
    // For sign-off, both diagnosed and correlated must be selected
    if (statusCode === 'SIGNEDOFF' && (!diagnosed || !correlated)) {
      // Make sure the user can see the controls they need to fill in
      setExpandedSections(prev => ({ ...prev, advancedOptions: true }));
      message.error('Please select both "Clinically Diagnosed" and "Clinically Correlated" before signing off');
      return;
    }

    const proxyUser = statusCode === 'SIGNEDOFF' ? proxySignoffUser : proxyDraftUser;

    // Convert radio button values to boolean
    const payload: any = {
      proxy_user: proxyUser,
      co_signing_doctor: cosigningDoctor,
    };

    if (diagnosed) {
      payload.diagnosed = diagnosed === 'diagnosed';
    }
    if (correlated) {
      payload.correlated = correlated === 'correlated';
    }

    saveReport(statusCode, payload);
  }, [saveReport, proxyDraftUser, proxySignoffUser, cosigningDoctor, diagnosed, correlated]);

  const handleSaveAsDraft = useCallback(async () => {
    const newDocId = await saveAsDraft(proxyDraftUser ?? undefined);
    if (newDocId) {
      setActiveDocId(newDocId);
      refetchReports();
    }
  }, [saveAsDraft, proxyDraftUser, refetchReports]);

  const handleLoadReport = useCallback((report: Report) => {
    const ooReport = report as OoReport;
    const docId = ooReport.document?.doc_id;
    if (!docId) return;

    if (ooReport.document?.doc_status === 'finalized') {
      // View a signed snapshot read-only; keep the editing context (the draft) intact so
      // Sign off still snapshots the working draft, not the report being previewed.
      setActiveDocId(docId);
    } else {
      // Switch the editing context to this draft.
      setCurrentReport(ooReport);
      setActiveDocId(docId);
    }
  }, [setCurrentReport]);

  const handleLoadPriorReport = useCallback(async (report: Report) => {
    const ooReport = report as OoReport;
    const newDocId = await copyPriorReport(ooReport);
    if (newDocId) {
      setActiveDocId(newDocId);
      refetchReports();
    }
  }, [copyPriorReport, refetchReports]);

  // ── Apply template to current document (backend handles append/replace, then reload editor) ──
  const [pendingTemplateId, setPendingTemplateId] = useState<number | null>(null);
  const [templateLoadDialogVisible, setTemplateLoadDialogVisible] = useState(false);

  const handleInsertTemplate = useCallback((templateId: number) => {
    if (!activeDocId) {
      message.error('No active document');
      return;
    }
    setPendingTemplateId(templateId);
    // Doc was just created in this session (no prior content) — auto-replace without asking
    if (isNewDocRef.current) {
      applyTemplate(templateId, 'replace');
    } else {
      setTemplateLoadDialogVisible(true);
    }
  }, [activeDocId]);

  const applyTemplate = useCallback(async (templateId: number, action: 'append' | 'replace') => {
    if (!activeDocId || !templateId) return;
    setTemplateLoadDialogVisible(false);
    try {
      await applyTemplateToDocument(activeDocId, templateId, action);
      isNewDocRef.current = false;
      destroyEditor();
      loadEditorConfig(activeDocId);
      message.success(`Template ${action === 'append' ? 'appended' : 'applied'}`);
    } catch (err: any) {
      console.error('Failed to apply template:', err);
      message.error(err.response?.data?.message || 'Failed to apply template');
    } finally {
      setPendingTemplateId(null);
    }
  }, [activeDocId, destroyEditor, loadEditorConfig]);

  const handleDeleteReport = useCallback(async (report: Report) => {
    const ooReport = report as OoReport;
    const isLastReport = reports.length <= 1;
    await deleteReport(ooReport);
    if (ooReport.document?.doc_id === activeDocId) {
      destroyEditor();
      setActiveDocId(null);
    }
    if (isLastReport) {
      handlePostSaving?.('DELETED', patientDetails);
    } else {
      refetchReports();
    }
  }, [deleteReport, refetchReports, activeDocId, destroyEditor, reports.length, handlePostSaving, patientDetails]);

  const handleCosigning = useCallback((val: string, _userData: any) => {
    setCosigningDoctor(val);
  }, []);

  const toggleSection = (key: string) =>
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // ── Render ──
  return (
    <div className={`editor-container${focusMode ? ' focus-mode' : ''}`}>
      {/* ── Pane 1: Studies ── */}
      <aside className={`report-pane pane-studies${studiesCollapsed ? ' collapsed' : ''}`}>
        {studiesCollapsed ? (
          <Tooltip title="Show studies" placement="right">
            <button type="button" className="rail-expand-btn" onClick={() => setStudiesCollapsed(false)} aria-label="Expand studies panel">
              <RightOutlined />
            </button>
          </Tooltip>
        ) : (
          <>
            <div className="rail-header">
              <span className="rail-header-title">
                <UnorderedListOutlined /> Studies{orders.length ? ` · ${orders.length}` : ''}
              </span>
              <Tooltip title="Hide panel" placement="right">
                <button type="button" className="rail-icon-btn" onClick={() => setStudiesCollapsed(true)} aria-label="Collapse studies panel">
                  <LeftOutlined />
                </button>
              </Tooltip>
            </div>
            <div className="rail-scroll">
              <StudyQueue orders={orders} selectedIndex={selectedIndex} loading={queueLoading} onSelect={onSelectOrder} />
            </div>
          </>
        )}
      </aside>

      {/* ── Pane 2: Study details ── */}
      <aside className={`report-pane pane-details${detailsCollapsed ? ' collapsed' : ''}`}>
        {detailsCollapsed ? (
          <Tooltip title="Show study details" placement="right">
            <button type="button" className="rail-expand-btn" onClick={() => setDetailsCollapsed(false)} aria-label="Expand details panel">
              <RightOutlined />
            </button>
          </Tooltip>
        ) : (
          <>
            <div className="rail-header">
              <span className="rail-header-title">
                <UserOutlined /> Study Details
              </span>
              <Tooltip title="Hide panel" placement="right">
                <button type="button" className="rail-icon-btn" onClick={() => setDetailsCollapsed(true)} aria-label="Collapse details panel">
                  <LeftOutlined />
                </button>
              </Tooltip>
            </div>
            <div className="rail-scroll">
              {patient ? (
                <>
                  {/* Patient summary */}
                  <div className="patient-card">
                    <div className="patient-card-name">
                      <UserOutlined className="patient-card-icon" />
                      <span>{patient?.pat_name}{patient?.pat_pin ? `, ${patient.pat_pin}` : ''}</span>
                    </div>
                    <div className="patient-card-meta">
                      {patient?.pat_sex || '-'} · {patient?.pat_dob && isValidDate(patient.pat_dob) ? moment(patient.pat_dob).fromNow(true) : '-'} · {pacs_order?.po_modality || '-'}
                    </div>
                    <div className="patient-card-meta">Ref: {pacs_order?.po_ref_doc || '-'}</div>
                    <div className="patient-card-actions">
                      {hasStudyViewingPermission(getUserDetails()) && (
                        <Button
                          className="rail-action-btn"
                          size="small"
                          onClick={async () => {
                            const w = await launchViewerOnDualMonitor(patientDetails!.ps_study_uid, patientDetails!);
                            if (!w) message.warning('Could not open viewer window');
                          }}
                        >
                          Launch Viewer
                        </Button>
                      )}
                      <Button
                        className="rail-action-btn"
                        size="small"
                        icon={<FolderOutlined />}
                        onClick={() => setNotesModalOpen(true)}
                      >
                        View Notes
                      </Button>
                    </div>
                  </div>

                  {/* Reports History */}
                  <div className="collapsible-section">
                    <div className="collapsible-header" onClick={() => toggleSection('reportsHistory')}>
                      <div className="collapsible-header-left"><BarChartOutlined className="collapsible-icon" /><span className="collapsible-title">Reports History</span></div>
                      {expandedSections.reportsHistory ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
                    </div>
                    {expandedSections.reportsHistory && (
                      <div className="collapsible-content" style={{ paddingTop: 0, paddingBottom: 0 }}>
                        <ReportHistory reports={reports} activeDocId={activeDocId} onLoad={handleLoadReport} onDelete={handleDeleteReport} />
                      </div>
                    )}
                  </div>

                  {/* Prior Reports */}
                  {priorReports.length > 0 && (
                    <div className="collapsible-section">
                      <div className="collapsible-header" onClick={() => toggleSection('priorReports')}>
                        <div className="collapsible-header-left"><BarChartOutlined className="collapsible-icon" /><span className="collapsible-title">Prior Reports</span></div>
                        {expandedSections.priorReports ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
                      </div>
                      {expandedSections.priorReports && (
                        <div className="collapsible-content" style={{ paddingTop: 0, paddingBottom: 0 }}>
                          <PriorReports reports={priorReports} isSignedOff={isSignedOff} onLoadAsTemplate={handleLoadPriorReport} />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Advanced Options */}
                  {REPORT_CONFIG.SHOW_ADVANCED_OPTIONS && (
                    <div className="collapsible-section">
                      <div className="collapsible-header" onClick={() => toggleSection('advancedOptions')}>
                        <div className="collapsible-header-left"><SettingOutlined className="collapsible-icon" /><span className="collapsible-title">Advanced Options</span></div>
                        {expandedSections.advancedOptions ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
                      </div>
                      {expandedSections.advancedOptions && (
                        <AdvancedOptions
                          radUsers={radUsers}
                          allUsers={allUsers}
                          onNotifyPhysician={() => setCriticalFindingVisible(true)}
                          onCosignChange={handleCosigning}
                          isSignedOff={isSignedOff}
                          currentUsername={getUserDetails()?.username}
                          onProxyDraftChange={(val) => { setProxyDraftUser(val); setProxySignoffUser(null); }}
                          onProxySignoffChange={(val) => { setProxySignoffUser(val); setProxyDraftUser(null); }}
                          diagnosed={diagnosed}
                          correlated={correlated}
                          onDiagnosedChange={setDiagnosed}
                          onCorrelatedChange={setCorrelated}
                        />
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="rail-empty">Select a study to begin</div>
              )}
            </div>
          </>
        )}
      </aside>

      {/* ── Editor (max space) ── */}
      <div className="editor-section">
        <div className="section-header">
          <div className="section-header-title">Medical Report</div>
        </div>

        {viewingSigned ? (
          <div className="signed-off-banner" style={{ backgroundColor: '#F0F5FF', border: '1px solid #ADC6FF', borderRadius: '4px', padding: '12px 16px', margin: '12px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ExclamationCircleOutlined style={{ color: '#2F54EB', fontSize: '16px' }} />
            <span style={{ color: '#1D39C4', fontSize: '14px', fontWeight: 500 }}>
              Viewing a signed report (read-only). To make changes, open the draft from Reports History.
            </span>
          </div>
        ) : isSignedOff && (
          <div className="signed-off-banner" style={{ backgroundColor: '#FFF7E6', border: '1px solid #FFD591', borderRadius: '4px', padding: '12px 16px', margin: '12px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ExclamationCircleOutlined style={{ color: '#FA8C16', fontSize: '16px' }} />
            <span style={{ color: '#873800', fontSize: '14px', fontWeight: 500 }}>
              This study is already signed off. You're editing an amendment draft — clicking Sign off creates a NEW signed report and preserves the previous one. Templates and history loading are disabled.
            </span>
          </div>
        )}

        <div className="section-content" style={{ padding: 0, position: 'relative' }}>
          {/* Overlay layer — React freely adds/removes nodes here.
              It is a sibling of the OO container layer, NOT a wrapper around it,
              so React never uses any OO-mutated node as an insertBefore reference. */}
          <div className="absolute inset-0" style={{ zIndex: 10, pointerEvents: 'none' }}>
            {!activeDocId && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#45556C', pointerEvents: 'auto' }}>
                {patientDetails ? 'Loading…' : 'Select a study to begin reporting'}
              </div>
            )}
            {activeDocId && editorLoading && !scriptError && !editorError && (
              <div className="flex items-center justify-center" style={{ height: '100%', background: 'rgba(0,0,0,0.3)', pointerEvents: 'auto' }}>
                <Spin size="large" tip="Loading editor..." />
              </div>
            )}
            {activeDocId && scriptError && (
              <div className="flex items-center justify-center bg-white" style={{ height: '100%', pointerEvents: 'auto' }}>
                <Alert type="error" message="Failed to load editor" description={scriptError} action={<Button onClick={retryScript}>Retry</Button>} />
              </div>
            )}
            {activeDocId && editorError && (
              <div className="flex items-center justify-center bg-white" style={{ height: '100%', pointerEvents: 'auto' }}>
                <Alert type="error" message="Editor Error" description={editorError} action={
                  <Button onClick={() => { setEditorError(null); if (activeDocId) loadEditorConfig(activeDocId); }}>Retry</Button>
                } />
              </div>
            )}
          </div>
          {/* OO container layer — this div and its children are NEVER conditional.
              React renders exactly one stable child here at all times, so it never
              calls insertBefore/removeChild on nodes that OnlyOffice has mutated. */}
          <div className="h-full w-full">
            <div id="oo-report-editor-container" ref={containerRef} style={{ height: '100%', width: '100%' }} />
          </div>
        </div>

        <div className="section-footer">
          {!isSignedOff && pacs_order && (
            <TemplatePanel
              pacsOrdId={pacs_order.pacs_ord_id}
              modality={pacs_order.po_modality}
              bodyPart={pacs_order.po_body_part}
              onInsertTemplate={handleInsertTemplate}
            />
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {onToggleFocusMode && (
              <Tooltip title={`${focusMode ? 'Exit' : 'Enter'} focus mode (Ctrl+Shift+F)`}>
                <Button
                  className="footer-btn"
                  type="default"
                  icon={focusMode ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                  onClick={onToggleFocusMode}
                />
              </Tooltip>
            )}
            <Button className="footer-btn" type="default" onClick={cancel}>Cancel</Button>
            <StatusActions
              statusList={statusList}
              statusLoading={statusLoading}
              currentReportStatus={currentReport?.pr_status}
              isSignedOff={isSignedOff}
              proxyMode={proxyMode}
              onAction={handleSaveForm}
              onSaveAsDraft={handleSaveAsDraft}
            />
          </div>
        </div>
      </div>

      {/* ── Dialogs ── */}
      <TemplateLoadDialog
        visible={templateLoadDialogVisible}
        onAppend={() => applyTemplate(pendingTemplateId, 'append')}
        onReplace={() => applyTemplate(pendingTemplateId, 'replace')}
        onCancel={() => { setTemplateLoadDialogVisible(false); setPendingTemplateId(null); }}
      />
      <CriticalFindingDialog
        visible={criticalFindingVisible}
        orderDetails={patientDetails}
        onClose={() => setCriticalFindingVisible(false)}
      />
      {patientDetails && (
        <ViewNotesModal
          open={notesModalOpen}
          details={patientDetails}
          onClose={() => setNotesModalOpen(false)}
        />
      )}
    </div>
  );
};

export default OOReportEditor;
